import { Booking, Resource } from '@/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';
import { isLinkedDemandBooking } from '@/lib/demand-booking';

interface DeleteBookingConfirmDialogProps {
  open: boolean;
  booking: Booking | null;
  resource?: Resource | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** For recurring bookings: delete only this occurrence */
  onDeleteOnlyThis?: () => void;
  /** For recurring bookings: delete this and all future occurrences */
  onDeleteAllFuture?: () => void;
  /** For linked demand bookings: delete only this booking */
  onDeleteOnlyThisDemand?: () => void;
  /** For linked demand bookings: delete all bookings linked to the accepted plan */
  onDeleteAllLinkedDemand?: () => void;
  /** For multi-resource bookings: delete only the selected resource's booking */
  onDeleteForThisResource?: () => void;
  /** For multi-resource bookings: delete booking for all resources */
  onDeleteForAllResources?: () => void;
}

export type DeleteBookingDialogMode = 'demand' | 'multi-resource' | 'recurring' | 'standard';

export function getDeleteBookingDialogMode(
  booking: Pick<Booking, 'resourceIds' | 'recurrenceGroupId' | 'source' | 'demandPlanGroupId'>
): DeleteBookingDialogMode {
  const isRecurring = !!booking.recurrenceGroupId;
  if (isLinkedDemandBooking(booking) && !isRecurring) return 'demand';
  if (booking.resourceIds && booking.resourceIds.length > 1 && !isRecurring) return 'multi-resource';
  if (isRecurring) return 'recurring';
  return 'standard';
}

export function DeleteBookingConfirmDialog({
  open,
  booking,
  resource,
  onConfirm,
  onCancel,
  onDeleteOnlyThis,
  onDeleteAllFuture,
  onDeleteOnlyThisDemand,
  onDeleteAllLinkedDemand,
  onDeleteForThisResource,
  onDeleteForAllResources,
}: DeleteBookingConfirmDialogProps) {
  if (!booking) {
    return null;
  }

  const dialogMode = getDeleteBookingDialogMode(booking);
  const isRecurring = dialogMode === 'recurring';
  const isLinkedDemand = dialogMode === 'demand';
  const isMultiResource = dialogMode === 'multi-resource';

  // Linked demand booking delete dialog. This branch precedes multi-resource and
  // recurring handling so the independent demand scope cannot be shadowed.
  if (isLinkedDemand && onDeleteOnlyThisDemand && onDeleteAllLinkedDemand) {
    return (
      <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Open Demand Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to delete only this booking, or all bookings linked to this accepted demand plan?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteOnlyThisDemand}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Only this booking
            </AlertDialogAction>
            <AlertDialogAction
              onClick={onDeleteAllLinkedDemand}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              All linked demand bookings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Multi-resource booking delete dialog
  if (isMultiResource && !isRecurring && onDeleteForThisResource && onDeleteForAllResources) {
    return (
      <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Warning
            </AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to delete for{' '}
              <span className="font-semibold text-foreground">
                {resource?.name || 'this resource'}
              </span>
              {' '}or all Resources?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteForThisResource}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Only for this resource
            </AlertDialogAction>
            <AlertDialogAction
              onClick={onDeleteForAllResources}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              All Resources
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Recurring booking delete dialog with three options
  if (isRecurring && onDeleteOnlyThis && onDeleteAllFuture) {
    return (
      <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recurring Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to delete this occurrence of the booking, or this and all future occurrences?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteOnlyThis}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Only this booking
            </AlertDialogAction>
            <AlertDialogAction
              onClick={onDeleteAllFuture}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              All future booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Standard (non-recurring) delete dialog
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Booking</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the booking for{' '}
            <span className="font-semibold text-foreground">
              &quot;{booking.projectName || booking.description || 'Booking'}&quot;
            </span>
            {resource?.name && (
              <>
                {' '}assigned to{' '}
                <span className="font-semibold text-foreground">
                  {resource.name}
                </span>
              </>
            )}
            ? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Delete Booking
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
